/**
 * gammaPromotionOrchestrator.ts — MER Algo Footprints v7.1B — Phase 6.8
 *
 * Runtime promotion routing helper for live scoring orchestration.
 *
 * ── Purpose ───────────────────────────────────────────────────────────────────
 * Reads the current active promotion profile, resolves the rollout decision for
 * a given symbol/setupType context, and returns the flags that should be used
 * for live scoring plus shadow context for parallel observation.
 *
 * ── Design ────────────────────────────────────────────────────────────────────
 * Pure read path — no side effects on profile state.
 * Called at the start of each influence pass (confidence or ranking) to get
 * the correct effective flags for that evaluation.
 *
 * ── Safety guarantees ────────────────────────────────────────────────────────
 * - Shadow mode: effectiveFlags = baseline, candidateFlags = candidate (observe only)
 * - Inactive/retired: effectiveFlags = baseline, candidateFlags = null
 * - Active: effectiveFlags = candidate.flags (validated and clamped before registration)
 * - Limited: effectiveFlags = candidate if in scope, baseline otherwise
 * - Unvalidated profiles cannot be the active profile (registry enforces this)
 *
 * ── Default behavior ─────────────────────────────────────────────────────────
 * When no non-baseline candidate is active (the default state), effectiveFlags
 * equals GAMMA_INFLUENCE_FLAGS exactly. No behavior change occurs.
 *
 * Placement: src/domains/market/engines/gamma/gammaPromotionOrchestrator.ts
 */

import type { GammaInfluenceFlags }  from './gammaInfluenceFlags';
import { GAMMA_INFLUENCE_FLAGS }     from './gammaInfluenceFlags';
import { getCurrentCandidate }       from './gammaPromotionProfile';
import { resolveRollout, RolloutDecision } from './gammaRolloutRouter';

// ─── Runtime context ──────────────────────────────────────────────────────────

export interface PromotionRuntimeContext {
  /** Flags to use for LIVE scoring. Always safe — baseline when no candidate active. */
  effectiveFlags:  GammaInfluenceFlags;
  /**
   * Candidate's flags, present only when a shadow/limited/active candidate exists.
   * In shadow mode: these differ from effectiveFlags (which stays baseline).
   * In limited/active: these equal effectiveFlags when in scope.
   * Use candidateFlags to run a shadow evaluation pass without affecting live scores.
   */
  candidateFlags:  GammaInfluenceFlags | null;
  /** Which rollout path was resolved. */
  rolloutDecision: RolloutDecision;
  /** ID of the profile consulted (may be 'baseline'). */
  profileId:       string;
  /** Human-readable routing reason for audit logging. */
  reason:          string;
  /** True when shadow mode is active (candidate is observed but not applied live). */
  isShadow:        boolean;
}

// ─── Orchestrator ─────────────────────────────────────────────────────────────

/**
 * Resolves live promotion context for a given symbol and setup type.
 *
 * This is the single call site for promotion routing in the scoring path.
 * Both buildSymbolContext (confidence) and dashboardView (ranking) call this.
 *
 * @param symbol    The symbol being evaluated
 * @param setupType The setup type string for scope matching in limited mode.
 *                  Use the top setup type when available; fall back to score.type.
 */
export function resolvePromotionContext(
  symbol:    string,
  setupType: string,
): PromotionRuntimeContext {
  const activeProfile = getCurrentCandidate();
  const routing       = resolveRollout(activeProfile, symbol, setupType);

  return {
    effectiveFlags:  routing.effectiveFlags,
    candidateFlags:  routing.candidateFlags,
    rolloutDecision: routing.rolloutDecision,
    profileId:       routing.profileId,
    reason:          routing.reason,
    isShadow:        routing.rolloutDecision === 'shadow',
  };
}

/**
 * Returns just the effective live flags for a given context.
 * Convenience wrapper when shadow evaluation is not needed.
 */
export function getEffectiveFlags(
  symbol:    string,
  setupType: string,
): GammaInfluenceFlags {
  return resolvePromotionContext(symbol, setupType).effectiveFlags;
}

/**
 * Returns the production-safe default flags.
 * Used as the fallback when orchestration is bypassed (e.g. in tests that
 * do not set up a promotion profile).
 */
export function getBaselineFlags(): GammaInfluenceFlags {
  return GAMMA_INFLUENCE_FLAGS;
}
